import type { GitHubLookupRefusal } from "@beacon/core/github/identities"
import type { HarvestReport, Refusal } from "@beacon/core/access"

const day = 24 * 60 * 60 * 1000

/** When a Member was last active, as People says it. */
export function describeLastActive(lastActiveAt: string | null, now: Date) {
  if (!lastActiveAt) return "Never"
  const days = Math.floor((now.getTime() - Date.parse(lastActiveAt)) / day)
  if (days < 1) return "In the last day"
  if (days > 30) return "Over a month ago"
  return days === 1 ? "1 day ago" : `${days} days ago`
}

/** The same as a fact of its own, as a stacked row on People writes it: "Active 3 days ago". */
export function describeActivity(lastActiveAt: string | null, now: Date) {
  if (!lastActiveAt) return "Never active"
  return `Active ${describeLastActive(lastActiveAt, now).toLowerCase()}`
}

/**
 * What filling a group from a Slack user group did: whom it added, how many of them it made
 * Members, and whom it left out and why.
 */
export function describeImport({
  added,
  joined,
  leftOut,
}: {
  added: number
  joined: number
  leftOut: number
}) {
  const newcomers = joined > 0 ? `, ${joined} of them new to Beacon` : ""
  const addedPart =
    added === 0
      ? "Everyone in it was already in the group."
      : `Added ${added} ${added === 1 ? "Member" : "Members"}${newcomers}.`
  if (leftOut === 0) return addedPart
  const leftOutPart =
    leftOut === 1
      ? "1 person in it is not on a company email, so was left out."
      : `${leftOut} people in it are not on a company email, so were left out.`
  return `${addedPart} ${leftOutPart}`
}

/** A count with its noun: "1 person", "3 people". */
const counted = (count: number, one: string, many: string) => `${count} ${count === 1 ? one : many}`

/**
 * What bringing in everyone did, Google Workspace's directory and Slack's together: how many it
 * made Members, how many it matched to the Member they already were by an email, how many Members'
 * Profiles it filled in, whom each directory left out and why, and whom it deactivated for leaving
 * every directory they were in. Google is named only on a stage whose Workspace is set up, and said
 * to be unreachable when it couldn't be read.
 */
export function describeHarvest({ google, slack, deactivated }: HarvestReport) {
  const read = typeof google === "string" ? [slack] : [google, slack]
  const total = (count: keyof typeof slack) => read.reduce((sum, report) => sum + report[count], 0)
  const admitted = total("admitted")
  const merged = total("merged")
  const updated = total("updated")
  const directories = typeof google === "string" ? "Slack" : "Google and Slack"
  const leftOut = (count: number, where: string, why: string) =>
    `${count} in ${where} ${count === 1 ? "was" : "were"} left out: ${why}.`
  return [
    admitted === 0
      ? `Everyone in ${directories} on a company email is already a Member.`
      : `Made ${counted(admitted, "person a Member", "people Members")}.`,
    merged > 0 && `Matched ${counted(merged, "person", "people")} to the Member they already were.`,
    updated > 0 && `Filled in ${counted(updated, "Member's Profile", "Members' Profiles")}.`,
    typeof google !== "string" &&
      google.leftOut > 0 &&
      leftOut(google.leftOut, "Google", "suspended, archived, or not on a company email"),
    slack.leftOut > 0 &&
      leftOut(
        slack.leftOut,
        "Slack",
        slack.leftOut === 1
          ? "a guest, a bot, or not on a company email"
          : "guests, bots, or not on a company email",
      ),
    google === "unavailable" && "Google couldn't be reached, so only Slack was read.",
    deactivated > 0 &&
      `Deactivated ${counted(deactivated, "Member", "Members")} every directory they were in no longer lists.`,
  ]
    .filter(Boolean)
    .join(" ")
}

// Keyed by core's own union, so a refusal the web API gains is a type error here until it has words.
const refusals: Record<Refusal, string> = {
  "not-an-admin": "Only an Admin can do that.",
  "not-found": "That no longer exists. Reload the page to see what does.",
  "invalid-name": "A group's name must have something in it, and at most 80 characters.",
  "invalid-role": "Beacon has no such role.",
  "name-taken": "Another group already has that name.",
  "last-admin": "Beacon needs at least one Admin, so this one stays.",
  "not-yours": "Only they can change that.",
  "invalid-profile":
    "Something in your Profile is too long, or the time zone isn't one Beacon knows.",
  "invalid-identity":
    "That isn't an email address, a mobile number with its country code (+27 82 123 4567), or a username of 3 to 32 letters, digits, dots, dashes and underscores.",
  "identity-taken": "Another Member already has that.",
  "email-unavailable": "Beacon can't send email right now, so it can't send the code. Try later.",
  "invalid-code": "That code isn't right. Check the email and try again.",
  "code-expired": "That code has expired. Send a new one.",
  "code-sent-recently": "Beacon sent a code there a moment ago. Wait a minute before asking again.",
  "too-many-unproven": "Prove or remove one of the emails you're adding before adding another.",
  "unverified-email": "Prove that email with its code before making it your primary.",
  "not-removable": "That came from a directory or an Admin, so it stays.",
  "last-way-in": "It's your last way to sign in, so it stays.",
}

/**
 * Why the web API refused an Admin's change, in words, naming the Member who holds an Identity
 * when the refusal says whose it is.
 */
export function describeAdminRefusal(refusal: string, holder?: { name: string }) {
  if (refusal === "identity-taken" && holder) return `${holder.name} already has that.`
  return githubLookupRefusals[refusal as GitHubLookupRefusal] ?? describeRefusal(refusal)
}

// Keyed by core's own union: what GitHub says of a login an Admin gives as a GitHub account.
const githubLookupRefusals: Record<GitHubLookupRefusal, string> = {
  "github-user-not-found": "GitHub has nobody by that login. Check its spelling on their profile.",
  "github-not-connected": "This Beacon has no GitHub App yet, so it can't look that login up.",
  "github-unavailable": "GitHub didn't answer. Nothing changed, so try again in a moment.",
}

/** Why the web API refused a change, in words — or a plain apology for anything else. */
export function describeRefusal(refusal: string) {
  return refusals[refusal as Refusal] ?? "Something went wrong. Try again."
}

/**
 * Why a rename was refused, said under the group's name once the old name is back in its place. A
 * taken name is named, so the sentence can't be read as saying the name now showing is the taken
 * one; any other refusal is said as `describeRefusal` says it.
 */
export function describeRenameRefusal(refusal: string, name: string) {
  if (refusal === "name-taken") return `Another group is already called “${name.trim()}”.`
  return describeRefusal(refusal)
}

/**
 * What adding Members to a group from its picker did, as the one toast after the adds: "Added 3
 * Members to Exec". The adds go one at a time, so `added` may be fewer than were chosen when one
 * was refused part way, which the refusal's own toast says.
 */
export function describeAdded(added: number, group: string) {
  return `Added ${added} ${added === 1 ? "Member" : "Members"} to ${group}`
}

/** The label of a group picker's confirming button: "Add 3 Members", or "Add Members" for none. */
export function describeAdding(chosen: number) {
  if (chosen === 0) return "Add Members"
  return `Add ${chosen} ${chosen === 1 ? "Member" : "Members"}`
}

/** How many a Member's group chips show before the rest fold into "+N". */
const chipsShown = 3

/**
 * A Member's groups as People's cell shows them: the first few as chips, and the rest
 * behind a "+N" whose tooltip names them.
 */
export function groupChipsOf<G>(groups: readonly G[]) {
  return { shown: groups.slice(0, chipsShown), folded: groups.slice(chipsShown) }
}

/** The Members not yet in a group, which its picker offers, by name. */
export function addableTo<M extends { id: string; name: string }>(
  group: { members: readonly { id: string }[] },
  members: readonly M[],
) {
  const inGroup = new Set(group.members.map((member) => member.id))
  return members
    .filter((member) => !inGroup.has(member.id))
    .toSorted((a, b) => a.name.localeCompare(b.name))
}

/**
 * The group the Groups page shows: the one the URL names, or the first when it names none or one
 * that has gone, as a group just deleted has.
 */
export function selectedGroupOf<G extends { id: string }>(groups: readonly G[], id: string | null) {
  return groups.find((group) => group.id === id) ?? groups[0]
}

/**
 * The Slack user groups a search leaves, matching the name or the @handle as typed, ignoring case
 * and a leading "@".
 */
export function userGroupsMatching<U extends { name: string; handle: string }>(
  userGroups: readonly U[],
  query: string,
) {
  const sought = query.trim().toLowerCase().replace(/^@/, "")
  if (!sought) return [...userGroups]
  return userGroups.filter(
    ({ name, handle }) =>
      name.toLowerCase().includes(sought) || handle.toLowerCase().includes(sought),
  )
}

/**
 * The Slack user group Fill the group acts on: the one chosen, while the search still shows it.
 * Once a search hides it the choice lapses, so the button never fills a group that isn't on screen.
 */
export function chosenWhileShown(chosen: string | null, shown: readonly { id: string }[]) {
  return chosen !== null && shown.some(({ id }) => id === chosen) ? chosen : null
}
